import { EnvironmentProviders, makeEnvironmentProviders, ErrorHandler } from '@angular/core';
import { ANALYTICS_LOGGING_API_CONFIG, AnalyticsLoggingConfig } from '../config';

/**
 * Provides the Analytics Logging Library with configuration
 * 
 * @param config - Configuration for the analytics logging library
 * @returns Environment providers for the library
 * 
 * @example
 * ```typescript
 * // In app.config.ts (Angular 19 standalone)
 * import { provideAnalyticsLoggingLib } from 'analytics-logging-lib';
 * import { environment } from './environments/environment';
 * 
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideRouter(routes),
 *     provideHttpClient(withInterceptors([authInterceptor])),
 *     provideAnalyticsLoggingLib({
 *       apiBaseUrl: environment.apiUrl,
 *       debug: !environment.production,
 *       autoLogErrors: true,
 *       enablePerformanceMonitoring: true,
 *       performanceThreshold: 3000
 *     })
 *   ]
 * };
 * ```
 * 
 * @example
 * ```typescript
 * // In a NgModule (legacy)
 * import { provideAnalyticsLoggingLib } from 'analytics-logging-lib';
 * 
 * @NgModule({
 *   providers: [
 *     provideAnalyticsLoggingLib({
 *       apiBaseUrl: 'http://localhost:3000',
 *       debug: true
 *     })
 *   ]
 * })
 * export class AppModule { }
 * ```
 * 
 * @example
 * ```typescript
 * // With automatic error handling
 * import { provideAnalyticsLoggingLib, GlobalErrorHandler } from 'analytics-logging-lib';
 * 
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideAnalyticsLoggingLib({
 *       apiBaseUrl: environment.apiUrl,
 *       autoLogErrors: true
 *     }),
 *     { provide: ErrorHandler, useClass: GlobalErrorHandler }
 *   ]
 * };
 * ```
 */
export function provideAnalyticsLoggingLib(config: AnalyticsLoggingConfig): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: ANALYTICS_LOGGING_API_CONFIG,
      useValue: config
    }
  ]);
}
